import { defineStore } from 'pinia';
import { Ref, computed, ref } from 'vue';

import {
  fetchActivityTimeline,
  fetchIndividualRecord,
} from '@/api/individualRecord';
import { MDM_PROPERTIES } from '@/constants/mdm';
import { captureException } from '@/errors';
import type {
  RecordFiltersType,
  RecordOwnBySectionType,
  RecordPaginationType,
  RecordPartnerType,
  RecordSectionParamType,
  RecordSectionType,
} from '@/types/individual_record';
import type { TimelineEvent } from '@/types/timeline';

const DEFAULT_LIMIT = 10;
const DEFAULT_TIMELINE_LIMIT = 50;

/* `all` is the sectionless response the Overview tab reads. */
const RECORD_SECTIONS: RecordSectionParamType[] = [
  'all',
  'master',
  'opportunities',
  'contacts',
];

type SectionStateType<Section extends RecordSectionParamType> = {
  own: RecordOwnBySectionType[Section] | null;
  partners: {
    items: RecordPartnerType<Section>[];
    pagination: RecordPaginationType;
  };
  page: number;
  limit: number;
  isLoading: boolean;
  hasLoaded: boolean;
  error: unknown;
  filters: RecordFiltersType;
};

type SectionsStateType = {
  [Section in RecordSectionParamType]: SectionStateType<Section>;
};

type FetchSectionOptionsType = {
  page?: number;
  limit?: number;
  isForced?: boolean;
};

type TimelineStateType = {
  items: TimelineEvent[];
  isLoading: boolean;
  hasLoaded: boolean;
  error: unknown;
};

/* Factories, not consts: reset() hands back a fresh object each time, and the
   caches and filters are mutated in place. A shared const would be mutated by
   the first record and reset would stop clearing anything. */
function emptyPagination(limit: number): RecordPaginationType {
  return {
    page: 1,
    limit,
    last_page: 1,
    next_href: null,
    total_count: 0,
    has_more: false,
  };
}

function emptySection<Section extends RecordSectionParamType>(
  limit = DEFAULT_LIMIT,
): SectionStateType<Section> {
  return {
    own: null,
    partners: { items: [], pagination: emptyPagination(limit) },
    page: 1,
    limit,
    isLoading: false,
    hasLoaded: false,
    error: null,
    filters: emptyFilters(),
  };
}

function emptySections(): SectionsStateType {
  return {
    all: emptySection(),
    master: emptySection(),
    opportunities: emptySection(),
    contacts: emptySection(),
  };
}

function emptyTimeline(): TimelineStateType {
  return { items: [], isLoading: false, hasLoaded: false, error: null };
}

function emptyFilters(): RecordFiltersType {
  return {
    partner_search: '',
    partner_org_ids: [],
    partner_tag_ids: [],
    partner_segments: [],
    partner_score_bucket: [],
  };
}

export const useIndividualRecordStore = defineStore('IndividualRecord', () => {
  const sourceId = ref<number | null>(null);
  const sourcePrimaryKey = ref<string | null>(null);
  const mdmType = ref<'account' | 'lead' | null>(null);
  const ourPopulationIds = ref<number[]>([]);
  /* The cast keeps `sections.value[section]` resolving through the mapped type
     for a generic section. `ref<SectionsStateType>()` returns
     `Ref<UnwrapRef<SectionsStateType>>`, which collapses the per-section
     shapes into a union and breaks the writes in fetchSection. */
  const sections = ref(emptySections()) as Ref<SectionsStateType>;
  const timeline = ref<TimelineStateType>(emptyTimeline());

  /* Guards against a slow response for a section overwriting a newer one. */
  const requestTokens: Record<RecordSectionParamType, number> = {
    all: 0,
    master: 0,
    opportunities: 0,
    contacts: 0,
  };
  let timelineToken = 0;

  const all = computed(() => sections.value.all);
  const master = computed(() => sections.value.master);
  const opportunities = computed(() => sections.value.opportunities);
  const contacts = computed(() => sections.value.contacts);

  const recordName = computed(() => {
    const fields = sections.value.master.own?.fields ?? [];
    const fieldByMdmProperty = (mdmProperty: string) =>
      fields.find((field) => field.mdm_property === mdmProperty);
    const nameField =
      mdmType.value === 'lead'
        ? (fieldByMdmProperty(MDM_PROPERTIES.lead.EMAIL) ??
          fieldByMdmProperty(MDM_PROPERTIES.lead.NAME))
        : fieldByMdmProperty(MDM_PROPERTIES.account.NAME);
    return String(nameField?.value ?? '');
  });

  function reset() {
    for (const section of RECORD_SECTIONS) requestTokens[section] += 1;
    timelineToken += 1;
    sections.value = emptySections();
    timeline.value = emptyTimeline();
    mdmType.value = null;
    ourPopulationIds.value = [];
  }

  function setRecord(nextSourceId: number, nextSourcePrimaryKey: string) {
    if (
      sourceId.value === nextSourceId &&
      sourcePrimaryKey.value === nextSourcePrimaryKey
    )
      return;
    reset();
    sourceId.value = nextSourceId;
    sourcePrimaryKey.value = nextSourcePrimaryKey;
  }

  async function fetchSection<Section extends RecordSectionParamType>(
    section: Section,
    { page, limit, isForced = false }: FetchSectionOptionsType = {},
  ) {
    const currentSourceId = sourceId.value;
    const currentSourcePrimaryKey = sourcePrimaryKey.value;
    if (!currentSourceId || !currentSourcePrimaryKey) return;

    const state = sections.value[section];
    const nextPage = page ?? state.page;
    const nextLimit = limit ?? state.limit;
    const hasPagingChange =
      nextPage !== state.page || nextLimit !== state.limit;
    if (state.hasLoaded && !isForced && !hasPagingChange) return;

    state.page = nextPage;
    state.limit = nextLimit;

    const token = requestTokens[section] + 1;
    requestTokens[section] = token;
    state.isLoading = true;
    state.error = null;

    const { data, error } = await fetchIndividualRecord<Section>(
      currentSourceId,
      currentSourcePrimaryKey,
      {
        section: section === 'all' ? undefined : (section as RecordSectionType),
        page: state.page,
        limit: state.limit,
        ...state.filters,
      },
    );

    if (token !== requestTokens[section]) return;

    state.isLoading = false;
    if (error || !data) {
      state.error = error || new Error(`Could not load ${section} record data`);
      captureException(state.error);
      return;
    }

    state.own = data.own as RecordOwnBySectionType[Section];
    state.partners = data.partners as SectionStateType<Section>['partners'];
    state.hasLoaded = true;
    mdmType.value = data.mdm_type;
    ourPopulationIds.value = data.our_population_ids;
  }

  async function fetchTimeline({ limit = DEFAULT_TIMELINE_LIMIT } = {}) {
    const currentSourceId = sourceId.value;
    const currentSourcePrimaryKey = sourcePrimaryKey.value;

    timelineToken += 1;
    const token = timelineToken;
    timeline.value = emptyTimeline();
    if (!currentSourceId || !currentSourcePrimaryKey) return;

    timeline.value.isLoading = true;
    const { data, error } = await fetchActivityTimeline(
      currentSourceId,
      currentSourcePrimaryKey,
      { limit },
    );
    if (token !== timelineToken) return;

    timeline.value.isLoading = false;
    if (error || !data) {
      timeline.value.error =
        error || new Error('Could not load the activity timeline');
      captureException(timeline.value.error);
      return;
    }
    timeline.value.items = data.items ?? [];
    timeline.value.hasLoaded = true;
  }

  function setPage(section: RecordSectionParamType, page: number) {
    return fetchSection(section, { page, isForced: true });
  }

  async function setFilter<Key extends keyof RecordFiltersType>(
    section: RecordSectionParamType,
    key: Key,
    value: RecordFiltersType[Key],
  ) {
    const state = sections.value[section];
    state.filters[key] = value;
    state.page = 1;
    if (state.hasLoaded) await fetchSection(section, { isForced: true });
  }

  return {
    sourceId,
    sourcePrimaryKey,
    mdmType,
    ourPopulationIds,
    sections,
    timeline,
    all,
    master,
    opportunities,
    contacts,
    recordName,
    setRecord,
    fetchSection,
    fetchTimeline,
    setPage,
    setFilter,
    reset,
  };
});
